import React, { useEffect, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import {
  WriteEntityNode,
  WriteEntityNodeData,
} from "src/constants/NodeDataTypes";
import { useEntitySchemas } from "src/entities/queries";
import {
  EntityPropertiesCard,
  ID_KEY,
} from "src/entityActionNodes/EntityPropertiesCard";
import { LocateWriteEntityCard } from "src/entityActionNodes/LocateWriteEntityCard";
import { SelectEntityCard } from "src/entityActionNodes/SelectEntityCard";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { convertFieldErrorsBeToFe } from "src/utils/FieldErrorUtils";
import { useSubmitForm } from "src/utils/useSubmitForm";

type Props = {
  node: WriteEntityNode;
} & NodeEditorBaseProps<WriteEntityNodeData>;

export type WriteEntityNodeForm = WriteEntityNodeData & {};

export const WriteEntityNodeEditor: React.FC<Props> = ({
  node,
  immutable,
  isReactive,
  displayedError,
  onUpdate,
}) => {
  const formMethods = useForm<WriteEntityNodeForm>({
    defaultValues: node.data,
    ...(isReactive && { values: node.data }),
  });

  const { getValues, setValue } = formMethods;

  const { workspace } = useWorkspaceContext();
  const { data: entitySchemasPage, isLoading: isEntitySchemasLoading } =
    useEntitySchemas({
      baseUrl: workspace.base_url!,
    });

  const entitySchemas = useMemo(() => {
    return entitySchemasPage?.entities ?? [];
  }, [entitySchemasPage]);

  useSubmitForm({
    onChange: (newValues) => onUpdate({ newData: newValues }),
    disabled: isReactive,
    previousValues: node.data,
    watch: formMethods.watch,
  });

  // Automatically set the first entity schema if no entity schema is selected
  useEffect(() => {
    const entitySchema = getValues("entity_schema");
    if (!entitySchema && entitySchemas.length > 0) {
      setValue("entity_schema", entitySchemas[0]._id);
    }
  }, [entitySchemas, getValues, setValue]);

  const fieldErrors: FieldErrors = useMemo(
    () =>
      displayedError?.field_errors
        ? (convertFieldErrorsBeToFe(displayedError.field_errors) ?? {})
        : {},
    [displayedError],
  );

  return (
    <FormProvider {...formMethods}>
      <div className="space-y-4">
        <SelectEntityCard
          entitySchemas={entitySchemas}
          immutable={immutable}
          isLoading={isEntitySchemasLoading}
          title="Select the Entity type to create or update"
          onChange={() => {
            setValue("properties_to_write", [ID_KEY]);
          }}
        />
        <LocateWriteEntityCard
          fieldErrors={fieldErrors}
          immutable={immutable}
        />
        <EntityPropertiesCard
          entitySchemas={entitySchemas}
          immutable={immutable}
        />
      </div>
    </FormProvider>
  );
};
